import { ErrorAlert } from "@components/ErrorAlert";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback } from "react";
import { PayeeEngagementType, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { PublicCommentField } from "../../components/PublicCommentField";
import { selectorWorkLogWorkerNoun } from "../../selectors/selectorWorkLogWorkerNoun";

type FormData = {
  comment: string;
};

type Props = {
  workLog: WorkLog;
  contractorName?: string;
  /** Names the person the work log goes back to. */
  engagementType?: PayeeEngagementType | null;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalRequestChanges: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  engagementType,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const workerNoun = selectorWorkLogWorkerNoun(engagementType);
  const [requestChanges, requestChangesMeta] =
    queriesWorkLogs.requestWorkLogEdit.useMutation();

  const handleRequestChanges = useCallback(
    async (formData: FormData) => {
      if (!formData.comment.trim()) {
        openSnackbar({
          type: "warning",
          message: "Please provide details about the requested changes"
        });
        return;
      }

      // TODO(v3): requestWorkLogEdit carries the comment as `voidInvoiceRequest.reason`
      // (semantic shift from v1's public `comment`) — payer requesting changes on an
      // in-review work log; the field/route repurpose the void-invoice request shape.
      requestChanges(
        {
          workLogId: workLog.id!,
          voidInvoiceRequest: { reason: formData.comment }
        },
        {
          onSuccess: () => {
            modalProps.onClose({ success: true });
            openSnackbar({
              icon: { name: "check" },
              message: "Changes requested successfully"
            });
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to request changes. Please try again."
            });
          }
        }
      );
    },
    [openSnackbar, modalProps, requestChanges, workLog.id]
  );

  return (
    <WSModal title="Request changes" {...modalProps}>
      <WSForm<FormData>
        onSubmit={handleRequestChanges}
        defaultValues={{ comment: "" }}
      >
        <WSList gap="XL">
          <ErrorAlert
            role="alert"
            context="WorkLog"
            error={requestChangesMeta.error}
          />
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to request changes?
          </WSText.ParagraphSm>

          <WSAlert
            theme="info"
            title="About requesting changes on work logs"
            icon="info-circle"
          >
            {`This sends the work log back to the ${workerNoun} for corrections.`}
            They&apos;ll make changes and resubmit for your approval. Provide
            specific guidance below about what needs to change.
          </WSAlert>

          <PublicCommentField
            label="Comment *"
            description={`Comment is visible to both you and the ${workerNoun}. Please provide specific details that will inform ${contractorName} about the request`}
            placeholder="Provide specific details about the request"
          />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                label: "Yes, request changes",
                loading: requestChangesMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalRequestChanges = () =>
  useWSModalCustom(ModalRequestChanges);
